import DatePicker, { CustomDatePickerProps } from '../../inputs/DatePicker';

import { TID_ATTR } from '@/common/infrastructure/config/tid';
import { FormikFieldSkeleton } from './FormikFieldSkeleton';
import { useFormikField } from './hooks/useFormikField';
import { BaseFormikFieldProps } from './types/FormikFieldProps';
import { parseISODate } from '@/common/application/utils/timeUtils';
import { parse, isValid as isValidDate, isMatch } from 'date-fns';
import type { PickerChangeHandlerContext, DateValidationError } from '@mui/x-date-pickers/models';

type DatePickerChangeContext =
  | PickerChangeHandlerContext<DateValidationError>
  | { source: 'inputChange' | 'inputClear' | 'inputBlur'; validationError: DateValidationError };

export const FormikDatePicker = <T extends Record<string, unknown>>({
  id,
  label,
  view,
  required,
  onChange,
  testId,
  ...rest
}: BaseFormikFieldProps<T> & CustomDatePickerProps & { testId?: string }) => {
  const { status, setFieldValue, setFieldTouched, handleBlur, errorText, hasError, fieldValue } = useFormikField<T>({ id });

  const format = view === 'year' ? 'yyyy' : 'MM/dd/yyyy';
  // Convert string date to Date object if needed
  const dateValue = typeof fieldValue === 'string' && !isNaN(Date.parse(fieldValue)) ? parseISODate(fieldValue) : fieldValue;

  // Ensure value is never undefined (fix controlled/uncontrolled warning)
  const controlledValue = dateValue === undefined ? null : dateValue;

  const handleOnChange = async (date: Date | null, context?: PickerChangeHandlerContext<DateValidationError>) => {
    const normalized = date && isValidDate(date) ? date : null;
    await setFieldValue(id, normalized);
    await setFieldTouched(id, true);
    if (onChange && context) {
      onChange(normalized, context);
    } else if (onChange) {
      onChange(normalized, { validationError: null });
    }
  };

  if (status?.loading) {
    return <FormikFieldSkeleton />;
  }

  type RestProps = Omit<
    CustomDatePickerProps,
    'onChange' | 'value' | 'onBlur' | 'error' | 'helperText' | 'id' | 'label' | 'view' | 'format' | 'required'
  > & { [TID_ATTR]?: string };
  const { [TID_ATTR]: restTestId, slotProps: incomingSlotProps, ...restProps } = rest as RestProps;
  const baseTestId = testId ?? restTestId;

  type TextFieldSlotProps = {
    textField?: {
      inputProps?: Record<string, unknown>;
      slotProps?: Record<string, Record<string, unknown>>;
      [key: string]: unknown;
    };
    [key: string]: unknown;
  };
  const safeSlotProps = incomingSlotProps as TextFieldSlotProps | undefined;

  const textFieldSlotProps = baseTestId
    ? {
        inputLabel: { [TID_ATTR]: `${baseTestId}:label` },
        htmlInput: { [TID_ATTR]: `${baseTestId}:input` },
        formHelperText: { [TID_ATTR]: `${baseTestId}:helper-text` }
      }
    : undefined;

  const openPickerButtonSlotProps = baseTestId ? ({ [TID_ATTR]: `${baseTestId}:open-button` } as Record<string, string>) : undefined;

  const pickerPopupSlotProps = baseTestId
    ? {
        popper: { [TID_ATTR]: `${baseTestId}:popper` } as Record<string, string>,
        desktopPaper: { [TID_ATTR]: `${baseTestId}:desktop-paper` } as Record<string, string>,
        layout: { [TID_ATTR]: `${baseTestId}:layout` } as Record<string, string>,
        calendarHeader: { [TID_ATTR]: `${baseTestId}:calendar-header` } as Record<string, string>
      }
    : undefined;

  return (
    <DatePicker
      {...restProps}
      fullWidth
      id={id}
      name={id}
      label={label}
      value={controlledValue as Date | null}
      onChange={handleOnChange}
      onBlur={handleBlur}
      error={hasError}
      helperText={errorText}
      view={view}
      format={format}
      required={required}
      slotProps={
        {
          ...safeSlotProps,
          ...(openPickerButtonSlotProps && { openPickerButton: openPickerButtonSlotProps }),
          ...(pickerPopupSlotProps ?? {}),
          textField: {
            ...(safeSlotProps?.textField || {}),
            ...(textFieldSlotProps && {
              slotProps: {
                ...((safeSlotProps?.textField?.slotProps as Record<string, unknown>) || {}),
                ...textFieldSlotProps
              }
            }),
            inputProps: {
              ...(safeSlotProps?.textField?.inputProps || {}),
              ...(baseTestId ? { [TID_ATTR]: `${baseTestId}:input` } : {}),
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                const raw = e.target.value;
                if (!isMatch(raw, format)) return;

                const parsed = parse(raw, format, new Date());

                if (isValidDate(parsed)) {
                  setFieldValue(id, parsed);
                  if (onChange) {
                    onChange(parsed, { source: 'inputChange', validationError: null } as DatePickerChangeContext);
                  }
                }
              },
              onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                const raw = (e.target as HTMLInputElement).value;

                if (raw.trim() === '') {
                  setFieldValue(id, null);
                  if (onChange) {
                    onChange(null, { source: 'inputClear', validationError: null } as DatePickerChangeContext);
                  }
                  handleBlur(e);
                  return;
                }

                if (isMatch(raw, format)) {
                  const parsed = parse(raw, format, new Date());
                  if (isValidDate(parsed)) {
                    setFieldValue(id, parsed);
                    if (onChange) {
                      onChange(parsed, { source: 'inputBlur', validationError: null } as DatePickerChangeContext);
                    }
                  }
                  handleBlur(e);
                }
              }
            }
          } as unknown as CustomDatePickerProps['slotProps']
        } as unknown as CustomDatePickerProps['slotProps']
      }
    />
  );
};
